class ConnectionSimulator {
  private isSimulatingOffline = false
  private originalOnLine = navigator.onLine
  private originalFetch: typeof fetch
  private isIntercepted = false

  constructor() {
    this.originalFetch = window.fetch
    this.setupGlobalInterception()
    this.loadPersistedState()
  }

  // Load persisted state from localStorage
  private loadPersistedState(): void {
    try {
      const persistedOfflineMode = localStorage.getItem('connection_simulator_offline_mode')
      if (persistedOfflineMode === 'true') {
        this.simulateOffline()
      }
    } catch (error) {
      console.warn('Failed to load persisted connection simulator state:', error)
    }
  }

  // Setup global fetch interception
  private setupGlobalInterception(): void {
    if (this.isIntercepted) return

    const self = this
    window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      // Check if we're simulating offline mode
      if (self.isSimulatingOffline) {
        console.log('🌐 Blocking fetch request due to offline simulation:', input)
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Return a simulated network error response
        const errorResponse = new Response(
          JSON.stringify({
            error: 'Network Error',
            message: 'Simulated offline mode - no server communication allowed',
            simulated: true
          }),
          {
            status: 0, // Network error status
            statusText: 'Network Error',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        
        // Make the response throw an error when used
        Object.defineProperty(errorResponse, 'ok', { value: false })
        Object.defineProperty(errorResponse, 'status', { value: 0 })
        
        return errorResponse
      }
      
      // Normal fetch behavior
      return self.originalFetch.call(window, input, init)
    }

    this.isIntercepted = true
    console.log('🌐 Global fetch interception enabled')
  }

  // Simulate offline mode
  simulateOffline(): void {
    this.isSimulatingOffline = true
    // Override navigator.onLine
    Object.defineProperty(navigator, 'onLine', {
      writable: true,
      value: false
    })
    // Persist to localStorage
    try {
      localStorage.setItem('connection_simulator_offline_mode', 'true')
    } catch (error) {
      console.warn('Failed to persist offline mode state:', error)
    }
    console.log('🌐 Simulating offline mode - ALL network requests will be blocked')
  }

  // Simulate online mode
  simulateOnline(): void {
    this.isSimulatingOffline = false
    // Restore original navigator.onLine
    Object.defineProperty(navigator, 'onLine', {
      writable: true,
      value: this.originalOnLine
    })
    // Remove from localStorage
    try {
      localStorage.removeItem('connection_simulator_offline_mode')
    } catch (error) {
      console.warn('Failed to remove offline mode state:', error)
    }
    console.log('🌐 Simulating online mode - Network requests restored')
  }

  // Check if we're simulating offline
  isSimulatingOfflineMode(): boolean {
    return this.isSimulatingOffline
  }

  // Get current connection status (considering simulation)
  getConnectionStatus(): 'online' | 'offline' {
    if (this.isSimulatingOffline) {
      return 'offline'
    }
    return navigator.onLine ? 'online' : 'offline'
  }

  // Simulate network error for API calls (legacy method)
  async simulateNetworkError<T>(promise: Promise<T>): Promise<T> {
    if (this.isSimulatingOffline) {
      // Simulate network error
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate delay
      throw new Error('Network Error - Simulated offline mode')
    }
    return promise
  }

  // Get pending network requests count (for UI feedback)
  getPendingRequestsCount(): number {
    // This would need to be implemented with a request tracker
    // For now, return 0 as placeholder
    return 0
  }
}

export const connectionSimulator = new ConnectionSimulator() 